import React, { useEffect, useState } from 'react';

import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { IconEdit } from '@tabler/icons';
import { t } from 'i18next';
import { useParams } from 'react-router';
import { useSelector } from 'src/store/Store';
import UpdateTeamPositionForm from './updateTeamPosition';

import '../tournamentTeams/TournamentTeams.css';

import { useLazyGetTournamentPositionsQuery } from 'src/services/tournaments';
import useLang from 'src/hooks/useLang';
import CustomSelect from 'src/components/forms/theme-elements/CustomSelect';
import { Position } from 'src/services/tournaments/getTournamentPositions';

const TournamentPositions = ({ isLoading }: { isLoading: boolean }) => {
  const { id } = useParams();

  const { getTranslatedTitle } = useLang();

  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(undefined);
  const [openUpdateTeamPositionForm, setOpenUpdateTeamPositionForm] = useState<boolean>(false);
  const [selectedPosition, setSelectedPosition] = useState<Position | undefined>(undefined);

  const tournamentDetails = useSelector((state) => state.tournamentReducer.selectedTournament);

  const [getTournamentPositions, getTournamentPositionsRes] = useLazyGetTournamentPositionsQuery();

  useEffect(() => {
    if (!tournamentDetails) return;
    setSelectedCategory(tournamentDetails?.categories?.[0]?.id);
  }, [tournamentDetails]);

  useEffect(() => {
    if (!selectedCategory) return;
    loadPositions();
  }, [selectedCategory]);

  const loadPositions = () => {
    if (!tournamentDetails) return;
    getTournamentPositions({
      tournamentId: Number(id),
      categoryId: Number(selectedCategory),
    });
  };

  const HeaderToolbar = () => {
    return (
      <Box display={'flex'} flexDirection={'row'} py={1.5} alignItems={'center'}>
        
        {isLoading ? (
          <Skeleton sx={{ width: 250 }} variant="text" />
        ) : (
          <>
            <Tooltip title={t('TournamentTeams.category')}>
              <CustomSelect
                id="category_id"
                fullWidth
                onChange={(e: any) => setSelectedCategory(e.target.value)}
                value={selectedCategory}
                sx={{ flex: '0.5 0.5 18.5%' }}
              >
                {tournamentDetails?.categories?.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {getTranslatedTitle(option, 'name')}
                  </MenuItem>
                ))}
              </CustomSelect>
            </Tooltip>
          </>
        )}
        <Box sx={{ flex: '1 1 100%' }}>
          {isLoading ? <Skeleton sx={{ width: 150 }} variant="text" /> : <></>}
        </Box>
      </Box>
    );
  };

  if (isLoading || getTournamentPositionsRes?.isLoading || getTournamentPositionsRes?.isFetching)
    return (
      <Box display={'flex'} justifyContent={'center'} alignItems={'center'} width={'100%'}>
        <CircularProgress color="secondary" sx={{ m: 5 }} />
      </Box>
    );

  const data = getTournamentPositionsRes?.data?.data;

  return (
    <>
      <Box flexGrow={1}>
        <HeaderToolbar />
        <Divider />
      </Box>
      <Grid container spacing={2} mt={1}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="15%">
                  <Typography variant="h6">{t('TournamentPositions.teamId')}</Typography>
                </TableCell>
                <TableCell width="40%">
                  <Typography variant="h6">{t('TournamentPositions.team')}</Typography>
                </TableCell>
                <TableCell width="15%">
                  <Typography variant="h6">{t('TournamentPositions.teamNumber')}</Typography>
                </TableCell>
                <TableCell width="15%">
                  <Typography variant="h6">{t('TournamentPositions.position')}</Typography>
                </TableCell>
                <TableCell width="15%">
                  <Typography variant="h6">{t('Coaches.actions')}</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getTournamentPositionsRes.isLoading || getTournamentPositionsRes.isFetching
                ? Array.from({ length: 5 }).map((_, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {Array.from({ length: 5 }).map((_, cellIndex) => (
                        <TableCell key={cellIndex}>
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : data?.map((position) => (
                    <TableRow
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      key={position.id}
                    >
                      <TableCell>
                        <Typography variant="h6">{position.id}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle1">{position.players_names}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle1">{position.team_number}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle1">{position.position || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={t('TournamentPositions.editPosition')}>
                          <IconButton
                            color="info"
                            className="action-button-for-list"
                            onClick={() => {
                              setSelectedPosition(position);
                              setOpenUpdateTeamPositionForm(true);
                            }}
                          >
                            <IconEdit width={20} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <UpdateTeamPositionForm
        open={openUpdateTeamPositionForm}
        setOpen={setOpenUpdateTeamPositionForm}
        position={selectedPosition}
        loadPositions={loadPositions}
      />
    </>
  );
};

export default TournamentPositions;
